import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  buildLocalizedPath,
  extractLanguageFromPathname,
  normalizeLanguage,
} from '@/lib/routing';

const translations = {
  EN: {
    nav: {
      home: "Home", features: "Features", pricing: "Pricing",
      setup: "Set up your facility", signin: "Sign in",
    },
    hero: {
      badge: "Multi-tenant B2B Platform",
      headline1: "Control your slaughterhouse",
      headline2: "operations",
      headline3: "in real time.",
      subtext: "Track intake, weights, processing, and compliance in one system.",
      ctaPrimary: "Set up your facility",
      ctaSecondary: "Sign in",
      imageAlt: "Fresh meat hanging at a butcher shop stall",
    },
    features: {
      title: "Built for industrial operations",
      subtitle: "Every workflow. Every station. Every log.",
      blocks: [
        { title: "Operations Tracking", items: ["Intake logging", "Batch / lot tracking", "Workflow visibility"] },
        { title: "Scale Integration", items: ["Live weight capture", "Station-based tracking", "Real-time sync"] },
        { title: "Compliance & Logs", items: ["Audit trails", "Traceability", "Inspection readiness"] },
      ],
    },
    overview: {
      title: "One system for your entire plant",
      subtitle: "From the loading dock to the final report.",
      steps: [
        { label: "Intake", desc: "Log every animal at entry" },
        { label: "Processing", desc: "Track each step on the line" },
        { label: "Output", desc: "Weigh and label each carcass" },
        { label: "Reports", desc: "Audit-ready compliance logs" },
      ],
    },
    usecases: {
      title: "Real workflows. Real operations.",
      subtitle: "Designed for how slaughterhouses actually work.",
      cases: [
        { title: "Animal Intake", desc: "Log each animal at arrival. Record species, origin, and health status in seconds." },
        { title: "Slaughter Line Tracking", desc: "Monitor each station in real time. No paperwork. No gaps." },
        { title: "Carcass Labeling", desc: "Auto-generate labels with lot numbers, weight, and traceability codes." },
        { title: "Compliance Reporting", desc: "One-click audit reports. Export to PDF. Share with inspectors instantly." },
      ],
    },
    trust: {
      title: "Built for serious operations",
      subtitle: "Trust, by design",
      items: [
        { title: "Multi-tenant data isolation", desc: "Each facility's data is fully isolated. No cross-contamination." },
        { title: "Secure infrastructure", desc: "Encrypted at rest and in transit. SOC 2 compliant architecture." },
        { title: "Built for slaughterhouses", desc: "Not a generic tool. Designed specifically for meat processing." },
      ],
    },
    logos: {
      label: "Ready for the standards your auditors care about",
      aria: "Industry standards and integrations",
    },
    faq: {
      subtitle: "Frequently asked",
      title: "Answers before you ask",
      subcopy: "Real concerns from plant managers, IT teams, and quality officers who evaluate CarniTrack.",
      items: [
        {
          q: "How long does setup take?",
          a: "Most facilities go live within a week. We work with your team to map your stations, scales, and intake flow, then migrate logs — no rip-and-replace.",
        },
        {
          q: "Do you integrate with our existing scales and sensors?",
          a: "Yes. CarniTrack supports common industrial scale protocols (RS-232, OPC-UA, MQTT) and custom integrations via our API. Most plants keep their existing hardware.",
        },
        {
          q: "Is my data isolated from other tenants?",
          a: "Every tenant runs on a dedicated workspace with strict row- and request-level isolation. No cross-tenant reads, ever.",
        },
        {
          q: "What happens during an audit?",
          a: "One click exports a compliance-ready PDF or CSV with full traceability — lot numbers, weights, timestamps, and station history. Inspectors can review onsite or remotely.",
        },
        {
          q: "What if our internet goes down?",
          a: "Stations continue to log locally and sync automatically when connectivity returns. Nothing stops the line.",
        },
        {
          q: "How is pricing structured?",
          a: "Per facility, flat monthly rate based on throughput tier. No per-user fees. Enterprise plans are quoted for multi-plant operations.",
        },
      ],
    },
    finalCta: {
      badge: "Ready when you are",
      title: "Run your plant on live data, not paperwork.",
      subtitle: "Join 500+ facilities that replaced binders, spreadsheets, and radios with one operations system.",
      primary: "Set up your facility",
      secondary: "Sign in",
    },
    pricing: {
      title: "Simple, facility-based pricing",
      subtitle: "Pay per facility. Scale as you grow.",
      recommendedBadge: "Recommended",
      plans: [
        {
          name: "Starter", price: "$199", period: "/month",
          desc: "For small facilities getting started",
          features: ["Up to 100 animals/day", "Basic intake logging", "Scale integration (1 station)", "Email support"],
          cta: "Get started",
        },
        {
          name: "Professional", price: "$499", period: "/month",
          desc: "For mid-size operations",
          features: ["Up to 500 animals/day", "Full batch tracking", "Scale integration (5 stations)", "Compliance reports", "Priority support"],
          cta: "Get started", recommended: true,
        },
        {
          name: "Enterprise", price: "Custom", period: "",
          desc: "For large facilities and chains",
          features: ["Unlimited animals/day", "Multi-plant management", "Custom integrations", "Dedicated account manager", "SLA guarantee"],
          cta: "Contact us",
        },
      ],
    },
    setup: {
      title: "Register your company",
      subtitle: "Submit a request to create your CarniTrack workspace. Our team will review and approve it.",
      companyName: "Company name",
      companyNamePlaceholder: "e.g. Örnek Gıda Sanayi A.Ş.",
      ownerEmail: "Owner email",
      emailPlaceholder: "owner@yourcompany.com",
      password: "Password",
      passwordPlaceholder: "At least 8 characters, letters and numbers",
      passwordConfirm: "Confirm password",
      passwordConfirmPlaceholder: "Re-enter password",
      passwordMismatch: "Passwords do not match.",
      passwordWeak: "Use at least 8 characters with at least one letter and one number.",
      optionalDetails: "Optional company details",
      optionalToggleShow: "Add legal / contact details",
      optionalToggleHide: "Hide optional fields",
      companyFullName: "Legal company name",
      companyFullNamePlaceholder: "As on registry documents",
      companyAddress: "Address",
      companyAddressPlaceholder: "Street, city, country",
      contactPhone: "Phone",
      contactPhonePlaceholder: "+90 …",
      licenseNo: "License number",
      licenseNoPlaceholder: "If applicable",
      operationNo: "Operation number",
      operationNoPlaceholder: "If applicable",
      notes: "Notes",
      notesPlaceholder: "Anything we should know",
      cta: "Submit registration",
      submitting: "Submitting…",
      hasAccount: "Already have an account?",
      slugInfoTitle: "Workspace address",
      slugInfoBody: "Your workspace URL is derived from your company name on the server. You do not pick a short name or slug.",
    },
    registrationStatus: {
      title: "Registration status",
      heroTitle: "We're on it",
      heroSub: "Your workspace request is being reviewed. This page refreshes automatically.",
      loading: "Loading status…",
      workspacePreview: "Workspace address (preview)",
      statusLabel: "Status",
      unknown: "Unknown",
      pendingHint: "Pending approval. You can keep this page open — we'll show when you're approved.",
      approvedHint: "You can sign in with your owner email and password. If the app asks for your workspace, use the same email — discover-tenants will find your company.",
      rejectedHint: "This request was not approved. Contact your administrator or SamperLabs if you need help.",
      signInCta: "Sign in to CarniTrack",
      backSetup: "New registration",
      missingLink: "This link is invalid or incomplete. Go back to registration and submit again, or use the link from your confirmation email.",
      loadError: "Could not load status.",
    },
    signin: {
      title: "Sign in to CarniTrack",
      subtitle: "Access your facility dashboard",
      googleBtn: "Continue with Google",
      orText: "or sign in with email or phone",
      email: "Email", emailPlaceholder: "you@yourfacility.com",
      phone: "Phone", phonePlaceholder: "5551234567",
      password: "Password", passwordPlaceholder: "Your password",
      forgotPassword: "Forgot password?",
      cta: "Sign in",
      noAccount: "Don't have an account?",
      setupLink: "Set up your facility",
    },
    footer: {
      tagline: "Industrial operations software for meat processing facilities.",
      product: "Product", features: "Features", pricing: "Pricing", setup: "Set up facility",
      company: "Company", about: "About", contact: "Contact",
      legal: "Legal", privacy: "Privacy Policy", terms: "Terms of Service",
      copyright: "© 2025 CarniTrack. All rights reserved.",
      systemsOperational: "All systems operational",
    },
  },
  TR: {
    nav: {
      home: "Ana Sayfa", features: "Özellikler", pricing: "Fiyatlandırma",
      setup: "Tesisinizi kurun", signin: "Giriş yap",
    },
    hero: {
      badge: "Çok Kiracılı B2B Platformu",
      headline1: "Kesimhane operasyonlarınızı",
      headline2: "gerçek zamanlı",
      headline3: "kontrol edin.",
      subtext: "Hayvan kabulü, ağırlıklar, işleme ve uyumluluğu tek bir sistemde takip edin.",
      ctaPrimary: "Tesisinizi kurun",
      ctaSecondary: "Giriş yap",
      imageAlt: "Kasap tezgâhında asılı taze et",
    },
    features: {
      title: "Endüstriyel operasyonlar için tasarlandı",
      subtitle: "Her iş akışı. Her istasyon. Her kayıt.",
      blocks: [
        { title: "Operasyon Takibi", items: ["Hayvan kabul kaydı", "Parti / lot takibi", "İş akışı görünürlüğü"] },
        { title: "Terazi Entegrasyonu", items: ["Canlı ağırlık kaydı", "İstasyon tabanlı takip", "Gerçek zamanlı senkronizasyon"] },
        { title: "Uyumluluk ve Kayıtlar", items: ["Denetim izleri", "İzlenebilirlik", "Denetim hazırlığı"] },
      ],
    },
    overview: {
      title: "Tesisiniz için tek bir sistem",
      subtitle: "Yükleme rampasından son rapora kadar.",
      steps: [
        { label: "Kabul", desc: "Girişte her hayvanı kaydedin" },
        { label: "İşleme", desc: "Hattaki her adımı takip edin" },
        { label: "Çıktı", desc: "Her karkası tartın ve etiketleyin" },
        { label: "Raporlar", desc: "Denetime hazır uyumluluk kayıtları" },
      ],
    },
    usecases: {
      title: "Gerçek iş akışları. Gerçek operasyonlar.",
      subtitle: "Kesimhanelerin gerçekte nasıl çalıştığına göre tasarlandı.",
      cases: [
        { title: "Hayvan Kabulü", desc: "Varışta her hayvanı kaydedin. Tür, menşei ve sağlık durumunu saniyeler içinde kayıt altına alın." },
        { title: "Kesim Hattı Takibi", desc: "Her istasyonu gerçek zamanlı izleyin. Kağıt yok. Boşluk yok." },
        { title: "Karkas Etiketleme", desc: "Lot numaraları, ağırlık ve izlenebilirlik kodlarıyla otomatik etiket oluşturun." },
        { title: "Uyumluluk Raporlaması", desc: "Tek tıkla denetim raporları. PDF'e aktar. Müfettişlerle anında paylaş." },
      ],
    },
    trust: {
      title: "Ciddi operasyonlar için inşa edildi",
      subtitle: "Tasarımdan gelen güven",
      items: [
        { title: "Çok kiracılı veri yalıtımı", desc: "Her tesisin verileri tamamen yalıtılmıştır. Çapraz kirlenme yok." },
        { title: "Güvenli altyapı", desc: "Beklemede ve aktarımda şifrelenmiş. SOC 2 uyumlu mimari." },
        { title: "Kesimhaneler için tasarlandı", desc: "Genel bir araç değil. Et işleme için özel olarak tasarlandı." },
      ],
    },
    logos: {
      label: "Denetçilerinizin önemsediği standartlara hazır",
      aria: "Sektör standartları ve entegrasyonlar",
    },
    faq: {
      subtitle: "Sıkça sorulan",
      title: "Sormadan önce cevaplar",
      subcopy: "CarniTrack'i değerlendiren tesis müdürleri, BT ekipleri ve kalite sorumlularından gerçek sorular.",
      items: [
        {
          q: "Kurulum ne kadar sürüyor?",
          a: "Tesislerin çoğu bir hafta içinde canlıya geçer. Ekibinizle istasyonları, terazileri ve kabul akışını haritalandırır, kayıtları taşırız — her şeyi sıfırdan değiştirmeden.",
        },
        {
          q: "Mevcut terazi ve sensörlerimizle entegre oluyor mu?",
          a: "Evet. CarniTrack yaygın endüstriyel terazi protokollerini (RS-232, OPC-UA, MQTT) ve API aracılığıyla özel entegrasyonları destekler. Çoğu tesis mevcut donanımını korur.",
        },
        {
          q: "Verilerim diğer kiracılardan yalıtılmış mı?",
          a: "Her kiracı, satır ve istek düzeyinde sıkı yalıtıma sahip ayrılmış bir çalışma alanında çalışır. Kiracılar arası okuma asla yoktur.",
        },
        {
          q: "Denetim sırasında ne olur?",
          a: "Tek tıkla tam izlenebilirlikle — lot numaraları, ağırlıklar, zaman damgaları ve istasyon geçmişi — denetime hazır PDF veya CSV çıktısı alınır. Müfettişler sahada veya uzaktan inceleyebilir.",
        },
        {
          q: "İnternet bağlantımız kesilirse?",
          a: "İstasyonlar yerel olarak kayıt tutmaya devam eder ve bağlantı gelir gelmez otomatik olarak senkronize olur. Hat durmaz.",
        },
        {
          q: "Fiyatlandırma nasıl çalışıyor?",
          a: "Tesis başına, kapasite kademesine göre sabit aylık ücret. Kullanıcı başına ücret yok. Çok tesisli operasyonlar için kurumsal fiyat teklifi verilir.",
        },
      ],
    },
    finalCta: {
      badge: "Hazır olduğunuzda",
      title: "Tesisinizi kağıtla değil, canlı veriyle yönetin.",
      subtitle: "Dosya klasörlerini, tabloları ve telsizleri tek bir operasyon sistemiyle değiştiren 500+ tesise katılın.",
      primary: "Tesisinizi kurun",
      secondary: "Giriş yap",
    },
    pricing: {
      title: "Basit, tesis bazlı fiyatlandırma",
      subtitle: "Tesis başına ödeyin. Büydükçe ölçeklendirin.",
      recommendedBadge: "Önerilen",
      plans: [
        {
          name: "Başlangıç", price: "$199", period: "/ay",
          desc: "Başlangıç yapan küçük tesisler için",
          features: ["Günde 100 hayvana kadar", "Temel kabul kaydı", "Terazi entegrasyonu (1 istasyon)", "E-posta desteği"],
          cta: "Başla",
        },
        {
          name: "Profesyonel", price: "$499", period: "/ay",
          desc: "Orta ölçekli operasyonlar için",
          features: ["Günde 500 hayvana kadar", "Tam parti takibi", "Terazi entegrasyonu (5 istasyon)", "Uyumluluk raporları", "Öncelikli destek"],
          cta: "Başla", recommended: true,
        },
        {
          name: "Kurumsal", price: "Özel", period: "",
          desc: "Büyük tesisler ve zincirler için",
          features: ["Sınırsız hayvan/gün", "Çok tesis yönetimi", "Özel entegrasyonlar", "Özel hesap yöneticisi", "SLA garantisi"],
          cta: "Bize ulaşın",
        },
      ],
    },
    setup: {
      title: "Şirketinizi kaydedin",
      subtitle: "CarniTrack çalışma alanı için talep gönderin. Ekibimiz inceleyip onaylayacak.",
      companyName: "Şirket adı",
      companyNamePlaceholder: "örn. Örnek Gıda Sanayi A.Ş.",
      ownerEmail: "Sahip e-postası",
      emailPlaceholder: "sahip@sirketiniz.com",
      password: "Şifre",
      passwordPlaceholder: "En az 8 karakter, harf ve rakam",
      passwordConfirm: "Şifre tekrar",
      passwordConfirmPlaceholder: "Şifreyi yeniden girin",
      passwordMismatch: "Şifreler eşleşmiyor.",
      passwordWeak: "En az 8 karakter ve en az bir harf ile bir rakam kullanın.",
      optionalDetails: "İsteğe bağlı şirket bilgileri",
      optionalToggleShow: "Yasal / iletişim bilgileri ekle",
      optionalToggleHide: "İsteğe bağlı alanları gizle",
      companyFullName: "Ticari unvan",
      companyFullNamePlaceholder: "Sicil belgesindeki gibi",
      companyAddress: "Adres",
      companyAddressPlaceholder: "Sokak, şehir, ülke",
      contactPhone: "Telefon",
      contactPhonePlaceholder: "+90 …",
      licenseNo: "Lisans numarası",
      licenseNoPlaceholder: "Varsa",
      operationNo: "İşletme numarası",
      operationNoPlaceholder: "Varsa",
      notes: "Notlar",
      notesPlaceholder: "Bilmemiz gereken bir şey var mı?",
      cta: "Kaydı gönder",
      submitting: "Gönderiliyor…",
      hasAccount: "Zaten hesabınız var mı?",
      slugInfoTitle: "Çalışma alanı adresi",
      slugInfoBody: "Çalışma alanı URL'niz şirket adınızdan sunucu tarafında türetilir. Kısa ad veya slug seçmezsiniz.",
    },
    registrationStatus: {
      title: "Kayıt durumu",
      heroTitle: "İnceleniyor",
      heroSub: "Çalışma alanı talebiniz değerlendiriliyor. Bu sayfa otomatik yenilenir.",
      loading: "Durum yükleniyor…",
      workspacePreview: "Çalışma alanı adresi (önizleme)",
      statusLabel: "Durum",
      unknown: "Bilinmiyor",
      pendingHint: "Onay bekleniyor. Bu sayfayı açık tutabilirsiniz — onaylandığında gösterilecek.",
      approvedHint: "Sahip e-postanız ve şifrenizle giriş yapın. Uygulama çalışma alanı sorarsa aynı e-postayı kullanın; tenant keşfi şirketinizi bulur.",
      rejectedHint: "Bu talep onaylanmadı. Yardım için yöneticinize veya SamperLabs'a başvurun.",
      signInCta: "CarniTrack'e giriş yap",
      backSetup: "Yeni kayıt",
      missingLink: "Bağlantı geçersiz veya eksik. Kayda dönün veya onay e-postanızdaki bağlantıyı kullanın.",
      loadError: "Durum yüklenemedi.",
    },
    signin: {
      title: "CarniTrack'e giriş yap",
      subtitle: "Tesis panelinize erişin",
      googleBtn: "Google ile devam et",
      orText: "veya e-posta ya da telefon ile giriş yapın",
      email: "E-posta", emailPlaceholder: "siz@tesisiniz.com",
      phone: "Telefon", phonePlaceholder: "5551234567",
      password: "Şifre", passwordPlaceholder: "Şifreniz",
      forgotPassword: "Şifremi unuttum?",
      cta: "Giriş yap",
      noAccount: "Hesabınız yok mu?",
      setupLink: "Tesisinizi kurun",
    },
    footer: {
      tagline: "Et işleme tesisleri için endüstriyel operasyon yazılımı.",
      product: "Ürün", features: "Özellikler", pricing: "Fiyatlandırma", setup: "Tesis kur",
      company: "Şirket", about: "Hakkımızda", contact: "İletişim",
      legal: "Yasal", privacy: "Gizlilik Politikası", terms: "Kullanım Koşulları",
      copyright: "© 2025 CarniTrack. Tüm hakları saklıdır.",
      systemsOperational: "Tüm sistemler çalışıyor",
    },
  },
};

const LanguageContext = createContext(null);
const LANGUAGE_STORAGE_KEY = 'carnitrack.lang';

function readStoredLanguage() {
  if (typeof window === 'undefined') return 'EN';

  try {
    return normalizeLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return 'EN';
  }
}

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const routeLang = extractLanguageFromPathname(location.pathname);
  const [lang, setLangState] = useState(() => routeLang || readStoredLanguage());

  useEffect(() => {
    if (!routeLang || routeLang === lang) return;
    setLangState(routeLang);
  }, [lang, routeLang]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    } catch {
      /* ignore private mode / quota */
    }
  }, [lang]);

  const setLanguage = useCallback((nextLang) => {
    const normalized = normalizeLanguage(nextLang);
    setLangState(normalized);

    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    const localizedPath = buildLocalizedPath(currentPath, normalized);
    if (localizedPath !== currentPath) {
      navigate(localizedPath, { replace: true });
    }
  }, [location.hash, location.pathname, location.search, navigate]);

  const toggleLang = useCallback(() => {
    setLanguage(lang === 'EN' ? 'TR' : 'EN');
  }, [lang, setLanguage]);

  const pathFor = useCallback((path) => buildLocalizedPath(path, lang), [lang]);

  const value = useMemo(() => ({
    lang,
    pathFor,
    setLanguage,
    toggleLang,
    t: translations[lang],
  }), [lang, pathFor, setLanguage, toggleLang]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
